import type { TOC } from '@ember/component/template-only';
import {
  formatRating,
  formatPct,
  hasRatings,
  releaseYear,
  rtEmoji,
} from '../../utils/format';
import { rtSearchUrl, imdbTitleUrl } from '../../utils/external-urls';
import WatchProviders from './watch-providers';
import type { WatchProviderRegion } from './watch-providers';

interface Signature {
  Args: {
    data: Record<string, unknown> | null | undefined;
    watchRegion?: string;
    subscribedServices?: number[];
  };
}

interface MovieData {
  title?: string;
  overview?: string;
  releaseDate?: string;
  posterPath?: string;
  backdropPath?: string;
  imdbId?: string;
  genres?: string[];
  imdbRating?: number;
  rtRatingCritic?: number;
  rtRatingAudience?: number;
  metacriticRating?: number;
  watchProviders?: Record<string, WatchProviderRegion>;
}

function asMovieData(
  raw: Record<string, unknown> | null | undefined
): MovieData {
  return raw ?? {};
}

const MovieDetail: TOC<Signature> = <template>
  {{#let (asMovieData @data) as |d|}}
    <div class="flex flex-col gap-3">
      {{#if d.overview}}
        <p class="text-base-content/80 text-sm">{{d.overview}}</p>
      {{/if}}

      {{! Release year + genres }}
      <div class="flex flex-wrap gap-2">
        {{#if d.releaseDate}}
          <span class="badge badge-outline">{{releaseYear d.releaseDate}}</span>
        {{/if}}
        {{#each d.genres as |genre|}}
          <span class="badge badge-ghost badge-sm">{{genre}}</span>
        {{/each}}
      </div>

      {{! Ratings }}
      {{#if (hasRatings d)}}
        <div class="flex flex-wrap gap-3">
          {{#if d.imdbRating}}
            <div class="flex items-center gap-1">
              <span class="text-warning font-bold">★</span>
              <span class="text-sm font-semibold">{{formatRating
                  d.imdbRating
                }}</span>
              <span class="text-base-content/50 text-xs">IMDb</span>
            </div>
          {{/if}}
          {{#if d.rtRatingCritic}}
            <div class="flex items-center gap-1">
              <span>{{rtEmoji d.rtRatingCritic}}</span>
              <span class="text-sm font-semibold">{{formatPct
                  d.rtRatingCritic
                }}%</span>
              <span class="text-base-content/50 text-xs">RT Critics</span>
            </div>
          {{/if}}
          {{#if d.rtRatingAudience}}
            <div class="flex items-center gap-1">
              <span>{{rtEmoji d.rtRatingAudience}}</span>
              <span class="text-sm font-semibold">{{formatPct
                  d.rtRatingAudience
                }}%</span>
              <span class="text-base-content/50 text-xs">RT Audience</span>
            </div>
          {{/if}}
          {{#if d.metacriticRating}}
            <div class="flex items-center gap-1">
              <span class="text-sm font-semibold">{{d.metacriticRating}}</span>
              <span class="text-base-content/50 text-xs">Metacritic</span>
            </div>
          {{/if}}
        </div>
      {{/if}}

      {{! External links }}
      <div class="flex flex-wrap gap-2">
        {{#if d.imdbId}}
          <a
            href={{imdbTitleUrl d.imdbId}}
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-xs btn-outline gap-1"
          >
            IMDb ↗
          </a>
        {{/if}}
        {{#if d.title}}
          <a
            href={{rtSearchUrl d.title}}
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-xs btn-outline gap-1"
          >
            🍅 RT ↗
          </a>
        {{/if}}
      </div>

      {{! Watch Providers }}
      <WatchProviders
        @providers={{d.watchProviders}}
        @region={{@watchRegion}}
        @subscribedServices={{@subscribedServices}}
      />
    </div>
  {{/let}}
</template>;

export default MovieDetail;
